import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, Observable, interval, BehaviorSubject } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { CreditPlan, CreditPlanInterface } from 'src/app/model/credit-plan';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { CreditPlanChangeEntry } from 'src/app/model/credit-plan-change-entry';

/**
 * CreditPlanService is used to hold/move credit plan data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class CreditPlanService {
  /** A list of creditPlans the user has access to */
  private _creditPlans: CreditPlan[] = [];
  private _creditPlansSubject = new Subject<CreditPlan[]>();
  public creditPlans$ = this._creditPlansSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the creditPlan service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
    private _toastrService: ToastrService,
  ) {
    // Get the creditPlans when the token first loads
    this._userService.token$.subscribe(
      (token: string) => {
        if (token !== "") this._fetchCreditPlans();
      });

    // Continuously query the backend to receive new creditPlans
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchCreditPlans();
      });
  }

  /**
   * Get a list of credit plans
   * 
   * @return A reference to the current credit plans
   */
  public getCreditPlans(): CreditPlan[] {
    return this._creditPlans;
  }

  /**
   * Set the credit plans to a given array
   * 
   * @param creditPlans
   */
  private _setCreditPlans(creditPlans: CreditPlan[]): void {
    this._creditPlans = creditPlans.map(x => x.copyOf());
    this._creditPlansSubject.next(creditPlans.map(x => x.copyOf()));
  }

  /**
   * Update a credit plan
   * 
   * @param id The id of the credit plan to update
   * @param intf The creditPlan to update
   * @param updateNote The note to attach to the update
   */
  public updateCreditPlan(id: number, intf: CreditPlanInterface, updateNote: string): void {
    intf.id = id; // Update the id of the interface for addition to the list of creditPlans later

    // Find the current credit plan
    const currentPlan = this._creditPlans.find(x => x.id === id);
    if (currentPlan == undefined) {
      console.error(`[ERR]: cannot find credit plan with id ${id}`);
      return;
    }

    // Loop through each credit plan difference and update the credit plan using the API
    const differences = currentPlan.differences(new CreditPlan(intf));
    const updates: CreditPlanChangeEntry[] = [];
    for (let attr of differences.keys()) {
      // Get value to change
      const val = differences.get(attr);
      if (val == undefined) return;

      // Construct the interface to send to the backend
      updates.push({
        column: attr,
        value: val,
        notes: updateNote,
      });
    }

    // Send the update to the backend
    this._http.put<CreditPlanInterface>(environment.baseUrl + `/credit-plans/${id}`, updates).subscribe({
      next: (updatedPlan: CreditPlanInterface): void => {
        // Update the subject to have the new creditPlan on success
        const old: CreditPlan[] = this._creditPlans;
        for (let i = 0; i < old.length; i++) {
          if (old[i].id === updatedPlan.id) {
            old[i] = new CreditPlan(intf);
            break;
          }
        }

        this._setCreditPlans(old);
        this._toastrService.success("Successfully updated credit plan!");
      },
    });
  }

  /**
   * Create a new credit plan
   * 
   * @param intf the creditPlan to create 
   */
  public createCreditPlan(intf: CreditPlanInterface): void {
    this._http.post(environment.baseUrl + `/credit-plans`, intf).subscribe({
      next: (id: any): void => {
        intf.id = id as number;

        // Update the subject to have the new credit plan on success
        const old: CreditPlan[] = this._creditPlans;
        old.push(new CreditPlan(intf));

        this._setCreditPlans(old);
        this._toastrService.success("Successfully created credit plan!");
      },
    });
  }

  /**
   * Delete a credit plan
   * 
   * @unimplemented
   * @param intf the credit plan to delete 
   */
  public deleteCreditPlan(intf: CreditPlanInterface): void {
    /*
    this._http.delete(environment.baseUrl + `/creditPlans/${intf.id}`).subscribe({
      next: (_: any): void => {
        // Update the subject to have the new creditPlan on success
        this._setCreditPlans(this._creditPlans.filter(x => x.id !== intf.id));
        this._toastrService.success("Successfully deleted creditPlan!");
      },
    });
    */
  }

  /**
   * Get credit plans from the backend. You should subscribe to the
   * 'creditPlans$' field to receive updates
   */
  private _fetchCreditPlans(): void {
    this._http.get(environment.baseUrl + "/credit-plans").subscribe({
      next: (data: Object): void => {
        // Cast the data into a list of application interfaces
        const creditPlanInterfaces = data as CreditPlanInterface[];
        if (creditPlanInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into credit plan interface list`);
        }

        // Convert the interfaces to class values
        const creditPlans: CreditPlan[] = [];
        for (let intf of creditPlanInterfaces) {
          creditPlans.push(new CreditPlan(intf));
        }

        // Propagate changes if different
        const current = this._creditPlans;
        if (!arraysEqual(current, creditPlans)) {
          this._setCreditPlans(creditPlans);
          console.log(`[LOG]: successfully queried credit plans`);
        }
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

const arraysEqual = (a: CreditPlan[], b: CreditPlan[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (var i = 0; i < a.length; ++i) {
    if ((a[i] === null && a[i] !== b[i]) || !a[i].equals(b[i])) return false;
  }
  return true;
}

const delay = (delay: number) => {
  return new Promise(r => {setTimeout(r, delay)});
}